// src/pages/ServiceDetail/components/EventsTab.tsx

import React, { useState } from 'react';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { AnyService } from '../../../types/service.type';

interface Event {
  id: string;
  timestamp: Date;
  type: 'info' | 'warning' | 'error' | 'success';
  message: string;
  details?: string;
}

interface EventsTabProps {
  service: AnyService;
}

const EventsTab: React.FC<EventsTabProps> = ({ service }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');

  // Mock events
  const events: Event[] = [
    {
      id: '1',
      timestamp: new Date('2024-12-02T10:30:00'),
      type: 'error',
      message: 'Deployment failed: Database connection timeout',
      details: 'Unable to establish connection to MySQL database after 30 seconds',
    },
    {
      id: '2',
      timestamp: new Date('2024-12-02T10:25:00'),
      type: 'info',
      message: 'Starting deployment process',
      details: 'Preparing environment for deployment',
    },
    {
      id: '3',
      timestamp: new Date('2024-12-02T09:15:00'),
      type: 'warning',
      message: 'High memory usage detected',
      details: 'Memory usage at 85%. Consider upgrading instance type.',
    },
    {
      id: '4',
      timestamp: new Date('2024-12-02T08:00:00'),
      type: 'success',
      message: 'SSL certificate renewed successfully',
      details: 'Certificate valid until December 2025',
    },
    {
      id: '5',
      timestamp: new Date('2024-12-01T18:30:00'),
      type: 'info',
      message: 'Automatic backup completed',
      details: 'Backup size: 2.3 GB',
    },
  ];

  const filteredEvents = events
    .filter((event) => {
      const matchesSearch = event.message
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const matchesType = filterType === 'all' || event.type === filterType;
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      // Ordenar por fecha descendente (más recientes primero)
      return b.timestamp.getTime() - a.timestamp.getTime();
    });

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'error':
        return '🔴';
      case 'warning':
        return '⚠️';
      case 'success':
        return '✅';
      default:
        return 'ℹ️';
    }
  };

  const getEventColor = (type: string) => {
    switch (type) {
      case 'error':
        return 'text-red-700 bg-red-50 border-red-200';
      case 'warning':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      case 'success':
        return 'text-green-700 bg-green-50 border-green-200';
      default:
        return 'text-blue-700 bg-blue-50 border-blue-200';
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <SearchOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-sm bg-white dark:bg-gray-800 dark:text-white"
          />
        </div>

        <div className="flex items-center gap-2">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white dark:bg-gray-800 dark:text-white"
          >
            <option value="all">All Types</option>
            <option value="error">Errors</option>
            <option value="warning">Warnings</option>
            <option value="success">Success</option>
            <option value="info">Info</option>
          </select>

          <button className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <ReloadOutlined />
          </button>
        </div>
      </div>

      {/* Events List */}
      <div className="space-y-3">
        {filteredEvents.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No events found</p>
          </div>
        ) : (
          filteredEvents.map((event) => (
            <div
              key={event.id}
              className={`border rounded-lg p-4 ${getEventColor(event.type)}`}
            >
              <div className="flex items-start gap-3">
                <span className="text-xl flex-shrink-0 mt-0.5">
                  {getEventIcon(event.type)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium">{event.message}</p>
                    <span className="text-xs flex-shrink-0">
                      {new Date(event.timestamp).toLocaleString()}
                    </span>
                  </div>
                  {event.details && (
                    <p className="text-xs mt-1 opacity-80">{event.details}</p>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {filteredEvents.length > 0 && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredEvents.length} of {events.length} events
          </p>
          <div className="flex gap-2">
            <button className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed">
              Previous
            </button>
            <button className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors dark:text-gray-300">
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsTab;